import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  type OnDestroy,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StafferSettingsStoreService } from '@principle-theorem/ng-principle-shared';
import {
  MouseHoverBloc,
  SidebarManagerService,
} from '@principle-theorem/ng-shared';
import { snapshot } from '@principle-theorem/shared';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { auditTime, map, takeUntil, withLatestFrom } from 'rxjs/operators';

@Component({
  selector: 'pr-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SidebarComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  readonly expandedWidth: string = '275px';
  readonly minimisedWidth: string = '64px';
  @HostBinding('style.width') width: string = this.minimisedWidth;
  width$ = new BehaviorSubject<string>(this.minimisedWidth);
  @Output() widthChange = new EventEmitter<void>();
  sidebarClosedSet$: Observable<boolean>;

  constructor(
    private _element: ElementRef<HTMLElement>,
    public sidebar: SidebarManagerService,
    private _settings: StafferSettingsStoreService
  ) {
    const mouseHover = new MouseHoverBloc(this._element, this._onDestroy$);

    mouseHover.hover$
      .pipe(
        auditTime(100),
        withLatestFrom(
          this.sidebar.isDesktop$,
          this._settings.display$.pipe(
            map(({ sidebarClosed }) => sidebarClosed)
          )
        ),
        takeUntilDestroyed()
      )
      .subscribe(([isHovered, isDesktop, sidebarClosedSet]) => {
        if (sidebarClosedSet && isDesktop) {
          if (isHovered) {
            this.sidebar.expand();
          } else {
            this.sidebar.minimise();
          }
        }
      });

    this.sidebarClosedSet$ = this._settings.display$.pipe(
      map(({ sidebarClosed }) => sidebarClosed)
    );

    this._settings.display$
      .pipe(
        withLatestFrom(this.sidebar.isDesktop$),
        takeUntil(this._onDestroy$)
      )
      .subscribe(([display, isDesktop]) => {
        if (!isDesktop) {
          return;
        }

        if (display.sidebarClosed) {
          this.sidebar.minimise();
          return;
        }
        this.sidebar.expand();
      });

    this.sidebar.expanded$
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((expanded) => {
        this.width = expanded ? this.expandedWidth : this.minimisedWidth;
        this.width$.next(this.width);
        setTimeout(() => this.widthChange.emit());
      });
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  async toggleExpanded(): Promise<void> {
    const settings = await snapshot(this._settings.display$);
    if (settings.sidebarClosed) {
      this._settings.updateStafferSettings({
        display: { sidebarClosed: false },
      });
      return;
    }
    this._settings.updateStafferSettings({ display: { sidebarClosed: true } });
  }
}
